import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Copy, LayoutTemplate, X, Briefcase, Archive, RotateCcw, Pencil } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';
import api from '../../services/api';

const MODES = [
    { value: 'BOTH', label: 'Timer and manual entries' },
    { value: 'TIMER', label: 'Timer only' },
    { value: 'MANUAL', label: 'Manual entries only' },
];

// Theme-aware styles (the app has light "peach" and dark "turquoise" themes).
const field = { padding: '8px 10px', border: '1px solid var(--input-border, var(--border))', borderRadius: 6, fontSize: 14, background: 'var(--input-bg, var(--surface))', color: 'var(--text-main)' };
const ghost = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'transparent', color: 'var(--text-main)', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const muted = { color: 'var(--text-muted)', fontSize: 13 };
const card = { padding: 20, marginBottom: 20 };
const chip = { display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--border)', borderRadius: 16, padding: '3px 10px', fontSize: 13, color: 'var(--text-main)' };

const errorText = (e, fallback) => e.response?.data?.error || fallback;
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// ── New project / template dialog ───────────────────────────────────────────

const NewProjectDialog = ({ initial, clients, users, projects, onCancel, onCreate }) => {
    const [f, setF] = useState(initial);
    const templates = projects.filter(p => p.isTemplate);
    const realProjects = projects.filter(p => !p.isTemplate);
    const source = projects.find(p => p.id === f.fromProjectId);
    const sourceTasks = source ? source.tasks.filter(t => t.isActive).map(t => t.name) : [];

    const chooseSource = (id) => {
        const s = projects.find(p => p.id === id);
        setF({
            ...f, fromProjectId: id,
            // Prefill from the source; the user can still change everything.
            ...(s ? { isBillable: s.isBillable, isPublic: f.isTemplate ? false : s.isPublic, color: s.color, managerId: f.managerId || s.managerId || '' } : {}),
        });
    };

    const submit = (e) => {
        e.preventDefault();
        onCreate({
            name: f.name, clientId: f.clientId || null, managerId: f.managerId || null, color: f.color,
            isBillable: f.isBillable, isPublic: f.isTemplate ? false : f.isPublic, isTemplate: f.isTemplate,
            fromProjectId: f.fromProjectId || undefined, copyMembers: f.copyMembers, tasks: f.tasks,
        });
    };

    const kind = f.isTemplate ? 'template' : 'project';
    return (
        <div role="dialog" aria-label={`New ${kind}`} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
            <form onSubmit={submit} className="card" style={{ padding: 24, width: 560, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', display: 'grid', gap: 14, background: 'var(--surface)' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, flex: 1 }}>{f.isTemplate ? 'New template' : 'New project'}</h3>
                    <button type="button" aria-label="Close" onClick={onCancel} style={{ ...ghost, border: 'none' }}><X size={16} /></button>
                </div>

                <label style={{ display: 'grid', gap: 4 }}>Name
                    <input aria-label="Project name" autoFocus style={field} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder={f.isTemplate ? 'e.g. Standard website build' : 'e.g. Globex website redesign'} />
                </label>

                <label style={{ display: 'grid', gap: 4 }}>Start from
                    <select aria-label="Start from" style={field} value={f.fromProjectId} onChange={e => chooseSource(e.target.value)}>
                        <option value="">Blank {kind}</option>
                        {templates.length > 0 && <optgroup label="Templates">{templates.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>}
                        {realProjects.length > 0 && <optgroup label="Copy an existing project">{realProjects.map(p => <option key={p.id} value={p.id}>{p.name}{p.client ? ` (${p.client.name})` : ''}</option>)}</optgroup>}
                    </select>
                </label>

                {!f.isTemplate && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <label style={{ display: 'grid', gap: 4 }}>Client
                            <select aria-label="Client" style={field} value={f.clientId} onChange={e => setF({ ...f, clientId: e.target.value })}>
                                <option value="">No client (internal)</option>
                                {clients.filter(c => c.isActive || c.id === f.clientId).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </label>
                        <label style={{ display: 'grid', gap: 4 }}>Project manager
                            <select aria-label="Project manager" style={field} value={f.managerId} onChange={e => setF({ ...f, managerId: e.target.value })}>
                                <option value="">None</option>
                                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                        </label>
                    </div>
                )}

                <label style={{ display: 'grid', gap: 4 }}>
                    <span>Tasks {sourceTasks.length > 0 && <span style={muted}>(adds to the {sourceTasks.length} copied from “{source.name}”)</span>}</span>
                    <textarea aria-label="Tasks" rows={4} style={{ ...field, resize: 'vertical', fontFamily: 'inherit' }} value={f.tasks} onChange={e => setF({ ...f, tasks: e.target.value })}
                        placeholder={'One per line, or comma-separated:\nDesign\nDevelopment\nTesting, Deployment'} />
                </label>
                {sourceTasks.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{sourceTasks.map(t => <span key={t} style={chip}>{t}</span>)}</div>
                )}

                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                    <label><input type="checkbox" checked={f.isBillable} onChange={e => setF({ ...f, isBillable: e.target.checked })} /> Billable</label>
                    {!f.isTemplate && <label><input type="checkbox" checked={f.isPublic} onChange={e => setF({ ...f, isPublic: e.target.checked })} /> Open to everyone</label>}
                    {!f.isTemplate && source && !source.isTemplate && (
                        <label><input type="checkbox" checked={f.copyMembers} onChange={e => setF({ ...f, copyMembers: e.target.checked })} /> Copy members ({source.members.length})</label>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Colour
                        <input aria-label="Colour" type="color" value={f.color} onChange={e => setF({ ...f, color: e.target.value })} style={{ width: 36, height: 28, border: 'none', background: 'transparent', padding: 0 }} />
                    </label>
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" style={ghost} onClick={onCancel}>Cancel</button>
                    <button type="submit" className="btn btn-primary" style={{ padding: '8px 16px', fontSize: 14 }} disabled={!f.name.trim()}>
                        <Plus size={14} /> {f.isTemplate ? 'Create template' : 'Create project'}
                    </button>
                </div>
            </form>
        </div>
    );
};

// ── One project (or template) row with its manage panel ─────────────────────

const ProjectRow = ({ p, isAdmin, clients, users, open, onToggle, run, onCopy, onSaveAsTemplate, onUse }) => {
    const [taskInput, setTaskInput] = useState('');
    const activeTasks = p.tasks.filter(t => t.isActive);
    const archivedTasks = p.tasks.filter(t => !t.isActive);

    const patch = (body) => run(() => api.patch(`/time/projects/${p.id}`, body), p.isTemplate ? 'Template updated' : 'Project updated');
    const toggleMember = (userId) => {
        const ids = new Set(p.members.map(m => m.id));
        if (ids.has(userId)) ids.delete(userId); else ids.add(userId);
        return run(() => api.put(`/time/projects/${p.id}/members`, { userIds: [...ids] }), 'Members updated');
    };
    const addTasks = async () => {
        if (!taskInput.trim()) return;
        const ok = await run(async () => {
            const res = await api.post(`/time/projects/${p.id}/tasks`, { names: taskInput });
            return res.data.skipped ? `Added ${plural(res.data.created, 'task')} (${res.data.skipped} already existed)` : `Added ${plural(res.data.created, 'task')}`;
        });
        if (ok) setTaskInput('');
    };
    const removeTask = (t) => run(async () => {
        try {
            await api.delete(`/time/tasks/${t.id}`);
            return `Removed “${t.name}”`;
        } catch (e) {
            if (e.response?.data?.code !== 'TASK_HAS_ENTRIES') throw e;
            if (!window.confirm(`“${t.name}” has time logged against it, so it can't be deleted. Archive it instead? (It stays in reports but can't be picked for new time.)`)) return null;
            await api.patch(`/time/tasks/${t.id}`, { isActive: false });
            return `Archived “${t.name}”`;
        }
    });

    const summary = p.isTemplate
        ? [plural(activeTasks.length, 'task'), p.isBillable ? 'Billable' : 'Non-billable', p.manager && `Default PM: ${p.manager.name}`]
        : [p.manager ? `PM: ${p.manager.name}` : 'No PM', p.isBillable ? 'Billable' : 'Non-billable', p.isPublic ? 'Open to everyone' : plural(p.members.length, 'member'), plural(activeTasks.length, 'task')];

    return (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ width: 12, height: 12, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                <strong style={{ color: 'var(--text-main)' }}>{p.name}</strong>
                {!p.isActive && <span style={{ ...chip, fontSize: 11 }}>Archived</span>}
                <span style={muted}>{summary.filter(Boolean).join(' · ')}</span>
                <span style={{ flex: 1 }} />
                {isAdmin && p.isTemplate && <button style={ghost} onClick={() => onUse(p)}><Plus size={13} /> Use template</button>}
                {isAdmin && !p.isTemplate && <button style={ghost} title="Copy this project" onClick={() => onCopy(p)}><Copy size={13} /> Copy</button>}
                {isAdmin && !p.isTemplate && <button style={ghost} title="Save as a reusable template" onClick={() => onSaveAsTemplate(p)}><LayoutTemplate size={13} /> Save as template</button>}
                <button style={ghost} onClick={onToggle} aria-expanded={open}>{open ? 'Close' : 'Manage'}</button>
            </div>

            {open && (
                <div style={{ marginTop: 12, padding: 14, borderRadius: 8, background: 'var(--accent-subtle)', display: 'grid', gap: 16 }}>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                        {isAdmin && !p.isTemplate && (
                            <label>Client:{' '}
                                <select style={field} value={p.clientId || ''} onChange={e => patch({ clientId: e.target.value || null })}>
                                    <option value="">No client (internal)</option>
                                    {clients.filter(c => c.isActive || c.id === p.clientId).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </label>
                        )}
                        {isAdmin && (
                            <label>{p.isTemplate ? 'Default PM' : 'Project manager'}:{' '}
                                <select style={field} value={p.managerId || ''} onChange={e => patch({ managerId: e.target.value || null })}>
                                    <option value="">None</option>
                                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                            </label>
                        )}
                        <label><input type="checkbox" checked={p.isBillable} onChange={e => patch({ isBillable: e.target.checked })} /> Billable</label>
                        {!p.isTemplate && <label><input type="checkbox" checked={p.isPublic} onChange={e => patch({ isPublic: e.target.checked })} /> Open to everyone</label>}
                        <label><input type="checkbox" checked={!p.isActive} onChange={e => patch({ isActive: !e.target.checked })} /> Archived</label>
                    </div>

                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Tasks</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                            {activeTasks.length === 0 && <span style={muted}>No tasks yet. They are optional; people can log time to the project itself.</span>}
                            {activeTasks.map(t => (
                                <span key={t.id} style={chip}>{t.name}
                                    <button aria-label={`Remove task ${t.name}`} title="Remove" onClick={() => removeTask(t)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 0 }}><X size={12} /></button>
                                </span>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <textarea aria-label="Add tasks" rows={taskInput.includes('\n') ? 4 : 1} style={{ ...field, flex: 1, resize: 'vertical', fontFamily: 'inherit' }} value={taskInput}
                                placeholder="Type a task and press Enter, or paste a list (one per line or comma-separated)"
                                onChange={e => setTaskInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addTasks(); } }} />
                            <button style={ghost} onClick={addTasks} disabled={!taskInput.trim()}><Plus size={13} /> Add</button>
                        </div>
                        {archivedTasks.length > 0 && (
                            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                <span style={muted}>Archived:</span>
                                {archivedTasks.map(t => (
                                    <button key={t.id} title="Restore" style={{ ...chip, opacity: 0.6, cursor: 'pointer', background: 'transparent' }}
                                        onClick={() => run(() => api.patch(`/time/tasks/${t.id}`, { isActive: true }), `Restored “${t.name}”`)}>
                                        <RotateCcw size={11} /> {t.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {!p.isTemplate && !p.isPublic && (
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>Members <span style={muted}>(who can log time to this project)</span></div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {users.map(u => (
                                    <label key={u.id} style={{ ...chip, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={p.members.some(m => m.id === u.id)} onChange={() => toggleMember(u.id)} /> {u.name}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Page ────────────────────────────────────────────────────────────────────

const TimeSetup = () => {
    const me = JSON.parse(localStorage.getItem('user') || '{}');
    const isAdmin = ['ADMIN', 'HR'].includes(me.role); // others here are project managers
    const [settings, setSettings] = useState(null);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [message, setMessage] = useState(null); // { ok, text }
    const [clientName, setClientName] = useState('');
    const [renaming, setRenaming] = useState(null); // { id, name }
    const [openId, setOpenId] = useState(null);
    const [dialog, setDialog] = useState(null); // initial values for NewProjectDialog
    const [showArchived, setShowArchived] = useState(false);

    const load = useCallback(async () => {
        try {
            const [s, c, p, u] = await Promise.all([
                api.get('/time/settings'), isAdmin ? api.get('/time/clients') : Promise.resolve({ data: [] }),
                api.get('/time/projects'), api.get('/admin/users'),
            ]);
            setSettings(s.data); setClients(c.data);
            setProjects(isAdmin ? p.data : p.data.filter(x => x.managerId === me.id));
            setUsers(u.data.filter(x => x.isActive !== false));
        } catch (e) {
            setMessage({ ok: false, text: errorText(e, 'Failed to load time tracking setup') });
        }
    }, [isAdmin, me.id]);

    useEffect(() => { load(); }, [load]);

    // Runs an API call, shows its result, reloads. The message is what `fn`
    // returns if that's a string, else `okText`; null means the user cancelled.
    // Returns whether it succeeded.
    const run = async (fn, okText) => {
        try {
            const result = await fn();
            if (result === null) return false;
            setMessage({ ok: true, text: typeof result === 'string' ? result : okText });
            await load();
            return true;
        } catch (e) {
            setMessage({ ok: false, text: errorText(e, 'Something went wrong') });
            return false;
        }
    };

    const saveSettings = (patch) => run(async () => { setSettings((await api.put('/time/settings', patch)).data); }, 'Settings saved');

    const addClient = (e) => {
        e.preventDefault();
        run(async () => { await api.post('/time/clients', { name: clientName }); setClientName(''); }, 'Client added');
    };

    const blank = { name: '', clientId: '', managerId: '', isBillable: true, isPublic: false, isTemplate: false, color: '#8b5cf6', fromProjectId: '', copyMembers: false, tasks: '' };
    const openNew = (overrides = {}) => setDialog({ ...blank, ...overrides });
    const create = (body) => run(async () => {
        await api.post('/time/projects', body);
        setDialog(null);
        return body.isTemplate ? 'Template created' : 'Project created';
    });
    const fromSource = (p, extra) => ({ fromProjectId: p.id, isBillable: p.isBillable, isPublic: p.isPublic, color: p.color, managerId: p.managerId || '', ...extra });

    const realProjects = projects.filter(p => !p.isTemplate && (showArchived || p.isActive));
    const templates = projects.filter(p => p.isTemplate);
    const archivedCount = projects.filter(p => !p.isTemplate && !p.isActive).length;
    const rowProps = (p) => ({
        p, isAdmin, clients, users, run, open: openId === p.id, onToggle: () => setOpenId(openId === p.id ? null : p.id),
        onCopy: (x) => openNew(fromSource(x, { name: `${x.name} (copy)`, clientId: x.clientId || '' })),
        onSaveAsTemplate: (x) => openNew(fromSource(x, { name: `${x.name} template`, isTemplate: true, isPublic: false })),
        onUse: (x) => openNew(fromSource(x, { name: '', isPublic: false })),
    });

    // Group projects under their client; projects without one go under "Internal".
    const groups = [
        ...clients.filter(c => c.isActive || showArchived || realProjects.some(p => p.clientId === c.id)).map(c => ({ client: c, items: realProjects.filter(p => p.clientId === c.id) })),
        { client: null, items: realProjects.filter(p => !p.clientId) },
    ];

    return (
        <div className="container" style={{ padding: '2rem 1rem', maxWidth: 1040 }}>
            <PageHeader crumbs={isAdmin ? crumbsFor('timeSetup') : undefined} backTo={isAdmin ? '/admin/settings' : '/time'} backLabel={isAdmin ? 'Back to Settings' : 'Time Tracker'} />
            <h1 style={{ marginBottom: 4 }}>{isAdmin ? 'Time Tracking Setup' : 'My Projects'}</h1>
            <p style={{ ...muted, fontSize: 15, marginTop: 0 }}>{isAdmin ? 'Clients and their projects, reusable templates, and how time is entered.' : 'Manage the members and tasks of the projects you are the project manager of.'}</p>

            {message && (
                <div role="status" style={{ padding: '10px 14px', borderRadius: 6, margin: '12px 0', background: message.ok ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)', color: message.ok ? '#10b981' : '#ef4444', border: `1px solid ${message.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
                    {message.text}
                </div>
            )}

            {isAdmin && settings && (
                <section className="card" style={card}>
                    <h3 style={{ marginTop: 0 }}>Entry mode</h3>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                        <label>How employees log time:{' '}
                            <select aria-label="Entry mode" style={field} value={settings.timeEntryMode} onChange={e => saveSettings({ timeEntryMode: e.target.value })}>
                                {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </label>
                        <label>Longest single entry (hours):{' '}
                            <input aria-label="Max entry hours" type="number" min="1" max="24" style={{ ...field, width: 80 }} defaultValue={settings.timeMaxEntryHours}
                                onBlur={e => Number(e.target.value) !== settings.timeMaxEntryHours && saveSettings({ timeMaxEntryHours: Number(e.target.value) })} />
                        </label>
                    </div>
                </section>
            )}

            {isAdmin ? (
                <>
                    <section className="card" style={card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                            <h3 style={{ margin: 0, flex: 1 }}>Clients &amp; projects</h3>
                            {archivedCount > 0 && <label style={muted}><input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} /> Show archived ({archivedCount})</label>}
                            <button className="btn btn-primary" style={{ padding: '8px 14px', fontSize: 14 }} onClick={() => openNew()}><Plus size={14} /> New project</button>
                        </div>
                        <form onSubmit={addClient} style={{ display: 'flex', gap: 8, margin: '12px 0 4px' }}>
                            <input aria-label="Client name" style={{ ...field, flex: 1 }} placeholder="Add a client, e.g. Globex Corp" value={clientName} onChange={e => setClientName(e.target.value)} />
                            <button style={ghost} type="submit" disabled={!clientName.trim()}><Plus size={13} /> Add client</button>
                        </form>

                        {groups.map(({ client, items }) => (client || items.length > 0) && (
                            <div key={client ? client.id : 'internal'} style={{ marginTop: 18 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingBottom: 6 }}>
                                    <Briefcase size={15} color="var(--primary)" />
                                    {renaming && client && renaming.id === client.id ? (
                                        <form style={{ display: 'flex', gap: 6 }} onSubmit={e => { e.preventDefault(); run(async () => { await api.patch(`/time/clients/${client.id}`, { name: renaming.name }); setRenaming(null); }, 'Client renamed'); }}>
                                            <input aria-label="Client new name" autoFocus style={field} value={renaming.name} onChange={e => setRenaming({ ...renaming, name: e.target.value })} />
                                            <button style={ghost} type="submit">Save</button>
                                            <button style={ghost} type="button" onClick={() => setRenaming(null)}>Cancel</button>
                                        </form>
                                    ) : (
                                        <strong style={{ fontSize: 16, color: 'var(--text-main)' }}>{client ? client.name : 'Internal (no client)'}</strong>
                                    )}
                                    {client && !client.isActive && <span style={{ ...chip, fontSize: 11 }}>Archived</span>}
                                    <span style={muted}>{plural(items.length, 'project')}</span>
                                    <span style={{ flex: 1 }} />
                                    {client && !renaming && <button style={{ ...ghost, border: 'none' }} title="Rename client" aria-label={`Rename ${client.name}`} onClick={() => setRenaming({ id: client.id, name: client.name })}><Pencil size={13} /></button>}
                                    {client && <button style={{ ...ghost, border: 'none' }} title={client.isActive ? 'Archive client' : 'Restore client'} aria-label={`${client.isActive ? 'Archive' : 'Restore'} ${client.name}`}
                                        onClick={() => run(() => api.patch(`/time/clients/${client.id}`, { isActive: !client.isActive }), client.isActive ? 'Client archived' : 'Client restored')}>
                                        {client.isActive ? <Archive size={13} /> : <RotateCcw size={13} />}</button>}
                                    <button style={ghost} aria-label={`New project for ${client ? client.name : 'internal'}`} onClick={() => openNew({ clientId: client ? client.id : '' })}><Plus size={13} /> Project</button>
                                </div>
                                {items.length === 0 && <div style={{ ...muted, padding: '6px 0 6px 23px' }}>No projects for this client yet.</div>}
                                <div style={{ paddingLeft: 23 }}>
                                    {items.map(p => <ProjectRow key={p.id} {...rowProps(p)} />)}
                                </div>
                            </div>
                        ))}
                    </section>

                    <section className="card" style={card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                            <LayoutTemplate size={16} color="var(--primary)" />
                            <h3 style={{ margin: 0, flex: 1 }}>Templates</h3>
                            <button style={ghost} onClick={() => openNew({ isTemplate: true })}><Plus size={13} /> New template</button>
                        </div>
                        <p style={{ ...muted, margin: '4px 0 8px' }}>A template is a reusable task list and settings. Use one to start a new project quickly, or save any project as a template with “Save as template”. Nobody logs time to a template.</p>
                        {templates.length === 0 && <div style={muted}>No templates yet.</div>}
                        {templates.map(p => <ProjectRow key={p.id} {...rowProps(p)} />)}
                    </section>
                </>
            ) : (
                <section className="card" style={card}>
                    {projects.length === 0 && <p style={muted}>You are not the project manager of any project.</p>}
                    {projects.map(p => <ProjectRow key={p.id} {...rowProps(p)} />)}
                </section>
            )}

            {dialog && (
                <NewProjectDialog initial={dialog} clients={clients} users={users} projects={projects}
                    onCancel={() => setDialog(null)} onCreate={create} />
            )}
        </div>
    );
};

export default TimeSetup;
